import type React from "react";
import {observer} from "mobx-react-lite";
import {useRef, useState} from "react";
import {Skeleton, Stack} from "@mui/material";
import {useOutletContext} from "react-router";
import {SupplierNamePicker} from "~/pages/supplier-360/components/SupplierNamePicker";
import {supplierNameOptionKey, type SupplierNameOption} from "~/pages/supplier-360/components/supplierNameOption";
import {useSupplierParentNamesQuery} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useSupplierParentNamesQuery";
import {useSupplierRelationshipsQuery} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useSupplierRelationshipsQuery";
import {useUpdateSupplierParentMutation} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useUpdateSupplierParentMutation";
import {authStore} from "~/stores/init";
import type {
    SupplierDetailOutletContext,
    SupplierRelationship,
} from "~/pages/supplier-360/types/Supplier360Types";

const PARENT_OPTION_COUNT = 20;

function subsidiaryParent(rows: SupplierRelationship[] | undefined): SupplierRelationship | undefined {
    return rows?.find(r => r.relationship_type === 'subsidiary');
}

export const ParentField: React.FC = observer(() => {
    const {entity} = useOutletContext<SupplierDetailOutletContext>();
    const entityId = entity.id;
    const databagId = entity.databag_id;

    const {data: relationships, isLoading} = useSupplierRelationshipsQuery(entityId);
    const {mutate: updateParent, isPending} = useUpdateSupplierParentMutation();
    const canEdit = authStore.hasEditPermission;

    const relationship = subsidiaryParent(relationships);
    const saved: SupplierNameOption = {
        entityId: relationship?.to_entity ?? null,
        name: relationship?.to_name ?? '',
    };
    const savedKey = supplierNameOptionKey(saved);

    const [draft, setDraft] = useState<SupplierNameOption>(saved);
    const [prevSavedKey, setPrevSavedKey] = useState(savedKey);
    if (prevSavedKey !== savedKey) {
        setPrevSavedKey(savedKey);
        setDraft(saved);
    }

    // The picker debounces and reports the term; the query itself belongs here.
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    // Names already used as a parent somewhere in this databag (§2.4), fetched only while open.
    const {data: parentNames, isFetching} = useSupplierParentNamesQuery(
        databagId,
        search,
        PARENT_OPTION_COUNT,
        isOpen,
    );

    const options: SupplierNameOption[] = (parentNames ?? [])
        // A supplier can't be its own parent.
        .filter(p => p.entity_id !== entityId)
        .map(p => ({entityId: p.entity_id, name: p.name}));

    /** Typing moves the draft to a free-text parent. */
    const setTypedName = (value: string) => setDraft({entityId: null, name: value});

    /** Guards against re-sending the same parent while the write is still in flight. */
    const lastSubmittedRef = useRef<string | undefined>(undefined);

    const commit = (option: SupplierNameOption) => {
        const next: SupplierNameOption = {entityId: option.entityId, name: option.name.trim()};
        const nextKey = supplierNameOptionKey(next);
        if (!canEdit || isPending || nextKey === savedKey || nextKey === lastSubmittedRef.current) return;
        lastSubmittedRef.current = nextKey;
        setDraft(next);
        updateParent(
            {entityId, relationship, to_name: next.name, to_entity: next.entityId},
            {
                onError: () => {
                    lastSubmittedRef.current = undefined;
                    setDraft(saved);
                },
            },
        );
    };

    return (
        <Stack>
            {isLoading ? (
                <Skeleton variant="rounded" width={320} height={36} />
            ) : (
                <SupplierNamePicker
                    options={options}
                    loading={isFetching}
                    onSearch={setSearch}
                    onOpenChange={setIsOpen}
                    // The Identity section is the one place a brand-new parent can be typed in:
                    // an unresolved parent is just free text on the relationship (§2.4).
                    allowAdd
                    label="Parent"
                    placeholder="Search existing parents or type a name"
                    emptyText="No matching parent supplier"
                    disabled={!canEdit || isPending}
                    busy={isPending}
                    inputValue={draft.name}
                    onInputChange={setTypedName}
                    onSelect={commit}
                    // Blur saves nothing — it just drops the half-typed name, so the field never
                    // shows something the server doesn't have. Saving is picking an option, the
                    // "+ Add" row, or Enter (MUI's freeSolo Enter goes through `onChange`).
                    onBlur={() => setDraft(saved)}
                    onEscape={() => setDraft(saved)}
                />
            )}
        </Stack>
    );
});
